import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./CardAccountManagement.css";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";

import { TextBox, NumberBox, SelectBox } from "devextreme-react";
import {
  FaPlus,
  FaEdit,
  FaCreditCard,
  FaTrash,
  FaTimes,
  FaArrowDown,
  FaArrowUp,
  FaExchangeAlt,
  FaShieldAlt,
  FaDollarSign,
  FaCog,
} from "react-icons/fa";
import {
  MdCheckCircle,
  MdCancel,
  MdTrendingDown,
  MdTrendingUp,
  MdBalance,
  MdSpeed,
  MdSecurity,
  MdAccountBalance,
} from "react-icons/md";

import {
  getAllCards,
  createCard,
  updateCard,
  deleteCard,
  toggleLowAmountCard,
  toggleHighAmountCard,
  setThresholdAmount,
  openAddModal,
  closeAddModal,
  clearError,
  setSelectedTrader,
  getTraders,
} from "../store";
import { showNotification } from "./NotificationProvider";

const CardAccountManagement = () => {
  const dispatch = useDispatch();
  const {
    cards,
    loading,
    error,
    isAddModalOpen,
    thresholdLoading,
    selectedTrader,
  } = useSelector((state) => state.card);
  const { traders } = useSelector((state) => state.trader);

  // Form states
  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [exp, setExp] = useState("");
  const [editingCard, setEditingCard] = useState(null);
  const [deleteConfirmCard, setDeleteConfirmCard] = useState(null);

  // Global threshold configuration
  const [thresholdModalOpen, setThresholdModalOpen] = useState(false);
  const [globalThreshold, setGlobalThreshold] = useState(0);
  const [tempThreshold, setTempThreshold] = useState(0);

  // Fetch traders and cards on mount and clear error
  useEffect(() => {
    dispatch(clearError());

    // Load traders first
    dispatch(getTraders())
      .unwrap()
      .then((res) => {
        if (process.env.NODE_ENV !== "production" && process.env.REACT_APP_DISABLE_LOGS !== "true") {
          console.log("Traders response:", res);
        }
        const tradersList = res.data.data.traders;
        if (process.env.NODE_ENV !== "production" && process.env.REACT_APP_DISABLE_LOGS !== "true") {
          console.log("Traders list:", tradersList);
        }
        // Auto-select first trader if none selected and traders exist
        if (tradersList.length > 0 && !selectedTrader) {
          if (process.env.NODE_ENV !== "production" && process.env.REACT_APP_DISABLE_LOGS !== "true") {
            console.log("Auto-selecting trader:", tradersList[0].traderId);
          }
          dispatch(setSelectedTrader(tradersList[0].traderId));
        } else if (tradersList.length === 0) {
          showNotification(
            "Warning",
            "warning",
            "No traders found for this user"
          );
        }
      })
      .catch((error) => {
        console.error("Error loading traders:", error);
        showNotification("Error", "danger", "Failed to load traders");
      });
  }, [dispatch, selectedTrader]);

  // Load cards when trader is selected
  useEffect(() => {
    if (selectedTrader) {
      dispatch(getAllCards()).catch((error) => {
        showNotification("Error", "danger", "Failed to load cards");
      });
    }
  }, [dispatch, selectedTrader]);

  // Filter cards by selected trader
  const filteredCards = cards.filter(
    (card) => card.traderId === selectedTrader
  );

  // Set global threshold from any card (they all have the same value)
  useEffect(() => {
    if (filteredCards.length > 0) {
      setGlobalThreshold(filteredCards[0].thresholdAmount || 0);
    } else {
      setGlobalThreshold(0);
    }
  }, [filteredCards]);

  // Show error notification when error state changes
  useEffect(() => {
    if (error) {
      showNotification("Error", "danger", error);
    }
  }, [error]);

  // Get active cards for selected trader
  const activeCards = filteredCards.filter(
    (card) => card.isActiveForLowAmount || card.isActiveForHighAmount
  );

  const lowAmountCard = filteredCards.find((card) => card.isActiveForLowAmount);
  const highAmountCard = filteredCards.find(
    (card) => card.isActiveForHighAmount
  );

  // Handle form validation
  const validateForm = () => {
    if (!cardName.trim()) {
      showNotification("Error", "danger", "Card name is required");
      return false;
    }
    if (!cardNumber.trim()) {
      showNotification("Error", "danger", "Card number is required");
      return false;
    }
    if (!exp.trim()) {
      showNotification("Error", "danger", "Expiry date is required");
      return false;
    }

    // Basic card number validation (16 digits with optional spaces)
    const cleanCardNumber = cardNumber.replace(/\s/g, "");
    if (!/^\d{16}$/.test(cleanCardNumber)) {
      showNotification("Error", "danger", "Card number must be 16 digits");
      return false;
    }

    // Validate expiry date format (MM/YY)
    if (!/^\d{2}\/\d{2}$/.test(exp)) {
      showNotification(
        "Error",
        "danger",
        "Expiry date must be in MM/YY format"
      );
      return false;
    }

    return true;
  };

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const cleanValue = value.replace(/\s/g, "");
    const formatted = cleanValue.replace(/(.{4})/g, "$1 ").trim();
    return formatted.slice(0, 19); // Max 16 digits + 3 spaces
  };

  // Format expiry date
  const formatExpiryDate = (value) => {
    const cleanValue = value.replace(/\D/g, "");
    if (cleanValue.length >= 2) {
      return cleanValue.slice(0, 2) + "/" + cleanValue.slice(2, 4);
    }
    return cleanValue;
  };

  // Handle card number input
  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.value);
    setCardNumber(formatted);
  };

  // Handle expiry date input
  const handleExpiryChange = (e) => {
    const formatted = formatExpiryDate(e.value);
    setExp(formatted);
  };

  // Handle add/edit card
  const handleSaveCard = async () => {
    if (!validateForm()) return;

    try {
      if (editingCard) {
        const updateData = {
          cardName: cardName.trim(),
          cardNumber: cardNumber.trim(),
          exp: exp.trim(),
        };
        await dispatch(
          updateCard({ id: editingCard._id, updates: updateData })
        ).unwrap();
        showNotification("Success", "success", "Card updated successfully");
        setEditingCard(null);
      } else {
        if (!selectedTrader) {
          showNotification("Error", "danger", "Please select a trader first");
          return;
        }
        const cardData = {
          cardName: cardName.trim(),
          cardNumber: cardNumber.trim(),
          exp: exp.trim(),
          traderId: selectedTrader,
          isActiveForLowAmount: false,
          isActiveForHighAmount: false,
          thresholdAmount: 0,
        };
        console.log("Creating card with data:", cardData);
        console.log("Selected trader:", selectedTrader);
        await dispatch(createCard(cardData)).unwrap();
        showNotification("Success", "success", "Card added successfully");
      }
      handleCloseModal();
    } catch (error) {
      showNotification("Error", "danger", error || "Operation failed");
    }
  };

  // Handle open add modal
  const handleOpenAddModal = () => {
    setCardName("");
    setCardNumber("");
    setExp("");
    setEditingCard(null);
    dispatch(openAddModal());
  };

  // Handle open edit modal
  const handleOpenEditModal = (card) => {
    setCardName(card.cardName);
    setCardNumber(card.cardNumber);
    setExp(card.exp || "");
    setEditingCard(card);
    dispatch(openAddModal());
  };

  // Handle close modal
  const handleCloseModal = () => {
    setCardName("");
    setCardNumber("");
    setExp("");
    setEditingCard(null);
    dispatch(closeAddModal());
  };

  // Handle toggle low amount activation
  const handleToggleLowAmount = async (cardId) => {
    const card = filteredCards.find((c) => c._id === cardId);

    // If trying to activate and there's already an active low amount card
    if (
      !card.isActiveForLowAmount &&
      lowAmountCard &&
      lowAmountCard._id !== cardId
    ) {
      showNotification(
        "Error",
        "danger",
        "Only one card can be active for low amounts. Please deactivate the current one first."
      );
      return;
    }

    try {
      const result = await dispatch(toggleLowAmountCard(cardId)).unwrap();
      showNotification("Success", "success", result.message);
    } catch (error) {
      showNotification(
        "Error",
        "danger",
        error || "Failed to toggle low amount activation"
      );
    }
  };

  // Handle toggle high amount activation
  const handleToggleHighAmount = async (cardId) => {
    const card = filteredCards.find((c) => c._id === cardId);

    // If trying to activate and there's already an active high amount card
    if (
      !card.isActiveForHighAmount &&
      highAmountCard &&
      highAmountCard._id !== cardId
    ) {
      showNotification(
        "Error",
        "danger",
        "Only one card can be active for high amounts. Please deactivate the current one first."
      );
      return;
    }

    try {
      const result = await dispatch(toggleHighAmountCard(cardId)).unwrap();
      showNotification("Success", "success", result.message);
    } catch (error) {
      showNotification(
        "Error",
        "danger",
        error || "Failed to toggle high amount activation"
      );
    }
  };

  // Handle open global threshold modal
  const handleOpenThresholdModal = () => {
    setTempThreshold(globalThreshold);
    setThresholdModalOpen(true);
  };

  // Handle save global threshold (update all cards for selected trader)
  const handleSaveThreshold = async () => {
    try {
      if (filteredCards.length === 0) {
        showNotification(
          "Warning",
          "warning",
          "No cards available for this trader. Add cards first."
        );
        return;
      }

      // Update threshold for all cards of selected trader (they share the same threshold)
      const updatePromises = filteredCards.map((card) =>
        dispatch(
          setThresholdAmount({
            cardId: card._id,
            thresholdAmount: tempThreshold,
          })
        ).unwrap()
      );

      await Promise.all(updatePromises);
      setGlobalThreshold(tempThreshold);
      showNotification(
        "Success",
        "success",
        "Global threshold updated successfully"
      );
      setThresholdModalOpen(false);
    } catch (error) {
      showNotification(
        "Error",
        "danger",
        error || "Failed to update threshold"
      );
    }
  };

  // Handle delete card
  const handleDeleteCard = (card) => {
    if (card.isActiveForLowAmount || card.isActiveForHighAmount) {
      showNotification(
        "Error",
        "danger",
        "Cannot delete active card. Please deactivate it first."
      );
      return;
    }
    setDeleteConfirmCard(card);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (deleteConfirmCard) {
      try {
        await dispatch(deleteCard(deleteConfirmCard._id)).unwrap();
        showNotification("Success", "success", "Card deleted successfully");
        setDeleteConfirmCard(null);
      } catch (error) {
        showNotification("Error", "danger", error || "Failed to delete card");
      }
    }
  };

  // Get card brand from number
  const getCardBrand = (cardNumber) => {
    const cleanNumber = cardNumber.replace(/\s/g, "");
    if (cleanNumber.startsWith("4")) return "visa";
    if (cleanNumber.startsWith("5") || cleanNumber.startsWith("2"))
      return "mastercard";
    if (cleanNumber.startsWith("3")) return "amex";
    return "default";
  };

  // Handle trader selection change
  const handleTraderChange = (e) => {
    if (process.env.NODE_ENV !== "production" && process.env.REACT_APP_DISABLE_LOGS !== "true") {
      console.log("Trader selection changed to:", e.value);
    }
    dispatch(setSelectedTrader(e.value));
  };

  // Display card number (show full for management purposes)
  const displayCardNumber = (cardNumber) => {
    return cardNumber;
  };

  return (
    <div className="card-account-management">
      {/* Header Section */}
      <div className="management-header">
        <div className="header-content">
          <div className="header-title">
            <FaShieldAlt className="header-icon" />
            <h2>StarMax Card Management</h2>
          </div>
          <div className="header-controls">
            <div className="trader-selection">
              <label className="trader-label">Select Trader:</label>
              <SelectBox
                items={traders}
                displayExpr="traderName"
                valueExpr="traderId"
                value={selectedTrader}
                onValueChanged={handleTraderChange}
                placeholder="Choose a trader..."
                showClearButton={false}
                width={200}
                className="trader-select"
              />
            </div>
            <Button
              variant="contained"
              startIcon={<FaPlus />}
              onClick={handleOpenAddModal}
              className="add-card-btn"
              disabled={!selectedTrader}
            >
              Add New Card
            </Button>
          </div>
        </div>

        {/* Active Cards Summary */}
        <div className="active-summary">
          <div className="summary-card total-cards">
            <MdBalance className="summary-icon" />
            <div className="summary-info">
              <span className="summary-label">Total Cards</span>
              <span className="summary-count">{filteredCards.length}</span>
              <span className="summary-detail">
                {filteredCards.length === 1 ? "Card" : "Cards"} Available
              </span>
            </div>
          </div>

          <div className="summary-card low-amount">
            <MdTrendingDown className="summary-icon" />
            <div className="summary-info">
              <span className="summary-label">Low Amount Card</span>
              <span className="summary-count">{lowAmountCard ? "1" : "0"}</span>
              <span className="summary-detail">
                {lowAmountCard ? lowAmountCard.cardName : "Not Set"}
              </span>
            </div>
          </div>

          <div className="summary-card high-amount">
            <MdTrendingUp className="summary-icon" />
            <div className="summary-info">
              <span className="summary-label">High Amount Card</span>
              <span className="summary-count">
                {highAmountCard ? "1" : "0"}
              </span>
              <span className="summary-detail">
                {highAmountCard ? highAmountCard.cardName : "Not Set"}
              </span>
            </div>
          </div>

          <div className="summary-card threshold">
            <FaDollarSign className="summary-icon" />
            <div className="threshold-info">
              <span className="summary-label">Global Threshold</span>
              <span className="summary-count">
                ₼{globalThreshold.toLocaleString()}
              </span>
            </div>
            <Button
              size="small"
              variant="text"
              onClick={handleOpenThresholdModal}
              className="threshold-btn"
              startIcon={<FaCog />}
              disabled={!selectedTrader || filteredCards.length === 0}
            >
              Configure
            </Button>
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="cards-grid">
        {filteredCards.map((card) => (
          <div
            key={card._id}
            className={`smart-card ${getCardBrand(card.cardNumber)} ${
              card.isActiveForLowAmount || card.isActiveForHighAmount
                ? "active"
                : "inactive"
            } ${card.isActiveForLowAmount ? "active-low" : ""} ${
              card.isActiveForHighAmount ? "active-high" : ""
            }`}
          >
            {/* Card Header */}
            <div className="card-header">
              <div className="header-left">
                <div className="card-brand">
                  <FaCreditCard className="brand-icon" />
                  <div className="card-name-header">{card.cardName}</div>
                </div>
              </div>
              <div className="card-status">
                <div className="card-actions">
                  <IconButton
                    onClick={() => handleOpenEditModal(card)}
                    className="action-btn edit"
                    size="medium"
                  >
                    <FaEdit />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteCard(card)}
                    className="action-btn delete"
                    size="medium"
                    disabled={
                      card.isActiveForLowAmount || card.isActiveForHighAmount
                    }
                  >
                    <FaTrash />
                  </IconButton>
                </div>
                {card.isActiveForLowAmount || card.isActiveForHighAmount ? (
                  <MdCheckCircle className="status-icon active" />
                ) : (
                  <MdCancel className="status-icon inactive" />
                )}
              </div>
            </div>

            {/* Card Number and Expiry */}
            <div className="card-number-row">
              <div className="card-number">
                {displayCardNumber(card.cardNumber)}
              </div>
              <div className="card-exp">EXP: {card.exp || "N/A"}</div>
            </div>

            {/* Card Balance */}
            <div className="card-balance-display">
              <MdAccountBalance className="balance-icon" />
              <div className="balance-amount">
                <span className="balance-label">Total Received Amount</span>
                <span className="balance-value">
                  ₼
                  {(card.balance || 0).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>

            {/* Activation Controls */}
            <div className="activation-controls">
              <div className="activation-row">
                <FormControlLabel
                  control={
                    <Switch
                      checked={card.isActiveForLowAmount}
                      onChange={() => handleToggleLowAmount(card._id)}
                      color="success"
                      size="small"
                    />
                  }
                  label={
                    <div className="switch-label">
                      <MdTrendingDown className="switch-icon" />
                      <span>Low Amount</span>
                    </div>
                  }
                  className="activation-switch low"
                />
              </div>

              <div className="activation-row">
                <FormControlLabel
                  control={
                    <Switch
                      checked={card.isActiveForHighAmount}
                      onChange={() => handleToggleHighAmount(card._id)}
                      color="success"
                      size="small"
                    />
                  }
                  label={
                    <div className="switch-label">
                      <MdTrendingUp className="switch-icon" />
                      <span>High Amount</span>
                    </div>
                  }
                  className="activation-switch high"
                />
              </div>
            </div>
          </div>
        ))}

        {!selectedTrader ? (
          <div className="no-cards">
            <FaCreditCard className="no-cards-icon" />
            <h3>Select a Trader</h3>
            <p>
              Please select a trader from the dropdown above to manage cards
            </p>
          </div>
        ) : filteredCards.length === 0 ? (
          <div className="no-cards">
            <FaCreditCard className="no-cards-icon" />
            <h3>No Cards Added</h3>
            <p>
              Add your first card to start managing C2C transactions for this
              trader
            </p>
          </div>
        ) : null}
      </div>

      {/* Add/Edit Card Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        className="card-modal"
      >
        <DialogTitle className="modal-title">
          {editingCard ? "Edit Card" : "Add New Card"}
          <IconButton
            onClick={handleCloseModal}
            className="close-button"
            size="small"
          >
            <FaTimes />
          </IconButton>
        </DialogTitle>

        <DialogContent className="modal-content">
          <div className="form-group">
            <label>Card Name</label>
            <TextBox
              value={cardName}
              onValueChanged={(e) => setCardName(e.value)}
              placeholder="Enter card name (e.g., Business Main Card)"
              width="100%"
              className="custom-textbox"
            />
          </div>

          <div className="form-group">
            <label>Card Number</label>
            <TextBox
              value={cardNumber}
              onValueChanged={handleCardNumberChange}
              placeholder="1234 5678 9012 3456"
              width="100%"
              className="custom-textbox"
              maxLength={19}
            />
            <small className="form-help">Enter 16-digit card number</small>
          </div>

          <div className="form-group">
            <label>Expiry Date</label>
            <TextBox
              value={exp}
              onValueChanged={handleExpiryChange}
              placeholder="MM/YY"
              width="100%"
              className="custom-textbox"
              maxLength={5}
            />
            <small className="form-help">
              Enter expiry date in MM/YY format
            </small>
          </div>
        </DialogContent>

        <DialogActions className="modal-actions">
          <Button
            onClick={handleCloseModal}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveCard}
            variant="contained"
            color="primary"
            disabled={loading}
            className="save-btn"
          >
            {editingCard ? "Update Card" : "Add Card"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global Threshold Configuration Modal */}
      <Dialog
        open={thresholdModalOpen}
        onClose={() => setThresholdModalOpen(false)}
        maxWidth="md"
        fullWidth
        className="threshold-modal"
      >
        <DialogTitle className="modal-title">
          Configure Limit Amount
          <IconButton
            onClick={() => setThresholdModalOpen(false)}
            className="close-button"
            size="large"
          >
            <FaTimes />
          </IconButton>
        </DialogTitle>

        <DialogContent className="modal-content">
          <div className="threshold-config">
            <div className="current-setup">
              <Typography variant="h6" className="setup-title">
                Current Setup
              </Typography>
              <div className="setup-cards">
                <div className="setup-card low">
                  <MdTrendingDown className="setup-icon" />
                  <div className="setup-info">
                    <span className="setup-label">Low Amount Card</span>
                    <span className="setup-value">
                      {lowAmountCard ? lowAmountCard.cardName : "Not Set"}
                    </span>
                  </div>
                </div>
                <div className="setup-card high">
                  <MdTrendingUp className="setup-icon" />
                  <div className="setup-info">
                    <span className="setup-label">High Amount Card</span>
                    <span className="setup-value">
                      {highAmountCard ? highAmountCard.cardName : "Not Set"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="threshold-explanation">
              <FaExchangeAlt className="explanation-icon" />
              <div className="explanation-text">
                <p>Set threshold amount for payment page:</p>
                <ul>
                  <li>
                    Amounts{" "}
                    <strong>below ₼{tempThreshold.toLocaleString()}</strong> →
                    Use Low Amount Card
                  </li>
                  <li>
                    Amounts{" "}
                    <strong>
                      at or above ₼{tempThreshold.toLocaleString()}
                    </strong>{" "}
                    → Use High Amount Card
                  </li>
                </ul>
              </div>
            </div>

            <div className="slider-container">
              <Typography variant="body2" className="slider-label">
                Amount Threshold: ₼{tempThreshold.toLocaleString()}
              </Typography>
              <Slider
                value={tempThreshold}
                onChange={(e, newValue) => setTempThreshold(newValue)}
                min={0}
                max={10000}
                step={100}
                marks={[
                  { value: 0, label: "₼0" },
                  { value: 2500, label: "₼2.5K" },
                  { value: 5000, label: "₼5K" },
                  { value: 7500, label: "₼7.5K" },
                  { value: 10000, label: "₼10K" },
                ]}
                className="threshold-slider"
              />
            </div>

            <div className="number-input">
              <Typography variant="body2">Exact Amount:</Typography>
              <NumberBox
                value={tempThreshold}
                onValueChanged={(e) => setTempThreshold(e.value || 0)}
                min={0}
                step={100}
                format="#,##0"
                className="threshold-input"
              />
            </div>
          </div>
        </DialogContent>

        <DialogActions className="modal-actions">
          <Button
            onClick={() => setThresholdModalOpen(false)}
            variant="outlined"
            color="secondary"
            size="large"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveThreshold}
            variant="contained"
            color="primary"
            disabled={thresholdLoading}
            className="save-btn"
          >
            Update Global Threshold
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmCard}
        onClose={() => setDeleteConfirmCard(null)}
        maxWidth="sm"
        className="delete-modal"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <p>
            Are you sure you want to delete the card "
            {deleteConfirmCard?.cardName}"? This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmCard(null)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CardAccountManagement;
